<script setup>
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import WhitelabelEmailWarning from "@/components/global-themed/WhitelabelEmailWarning.vue";
import {computed, ref, watch} from "vue";
import {kitName} from "@/composables/useLocalDomains";
import useEmailSettings from "@/composables/email-settings/useEmailSettings";

const {agencyKitEmailRecipientsQuery, saveAgencyKitEmailRecipientsMutation, settingsQuery} = useEmailSettings();
const {isLoading, data} = agencyKitEmailRecipientsQuery;
const {mutate: saveRecipients, isPending: saving} = saveAgencyKitEmailRecipientsMutation;

const availableUsers = computed(() => data.value?.available_users || []);
const missingWhitelabelSetup = computed(() => data.value?.missing_whitelabel_setup || false);
const currentRecipients = computed(() => data.value?.selected_recipients || []);
const selectedRecipients = ref([]);

const emailFrequencySetting = computed(() => settingsQuery.data.value?.email_frequency);
const isDisabled = computed(() => emailFrequencySetting.value === 'off');

const disabledSelectClasses = computed(() => {
  if (!isDisabled.value) {
    return {};
  }

  return {
    containerDisabled: '!tw-cursor-not-allowed !tw-bg-neutral-100 !tw-border-neutral-200',
    placeholder: '!tw-text-neutral-400',
    search: '!tw-text-neutral-400',
  };
});

watch([currentRecipients, isLoading], ([recipients, loading]) => {
  if (!loading && recipients) {
    selectedRecipients.value = recipients;
  }
}, {
  immediate: true,
});

const save = () => {
  if (isLoading.value || isDisabled.value) {
    return;
  }

  if (JSON.stringify(selectedRecipients.value) === JSON.stringify(currentRecipients.value)) {
    return;
  }

  saveRecipients({
    recipient_ids: selectedRecipients.value,
  });
};

const tooltipText = (user) => {
  if (!user.is_organisation_admin && !user.has_seo_agent_permission) {
    return `Only users with organisation admin and ${kitName.value} permissions can receive these emails`
  }

  if (!user.is_organisation_admin) {
    return 'Only users with organisation admin permissions can receive these emails';
  }

  if (!user.has_seo_agent_permission) {
    return `Only users with ${kitName.value} permissions can receive these emails`;
  }

  return null;
};

const selectOptions = computed(() => {
  return availableUsers.value.map(user => ({
    value: user.id,
    label: `${user.name} (${user.email})`,
    disabled: !user.is_organisation_admin || !user.has_seo_agent_permission,
    tooltip: tooltipText(user),
  }));
});
</script>

<template>
  <div class="tw-bg-white tw-rounded-lg tw-p-8 tw-flex tw-flex-col tw-gap-8">
    <div class="tw-flex tw-flex-col tw-gap-6">
      <div class="tw-flex tw-justify-between tw-items-start tw-flex-col tw-gap-6">
        <div class="tw-flex tw-flex-col tw-gap-2 tw-flex-1">
          <p class="tw-font-bold tw-text-neutral-900 tw-text-2.5xl">
            {{ kitName }} Email Recipients
          </p>

          <p class="tw-text-neutral-900 tw-text-base tw-leading-snug">
            Choose which users should receive {{ kitName }} emails for your agency. You can select multiple users who will receive notifications based on the email frequency settings configured above. Only users in your agency can be selected as recipients.
          </p>
        </div>

        <WhitelabelEmailWarning v-if="missingWhitelabelSetup">
          <span class="tw-text-base tw-text-neutral-900">
            {{ kitName }} emails will not be delivered for your agency until white label email has been configured. You can still set up email preferences and recipients now, and delivery will begin automatically once setup is complete. Until configuration is finished, some emails sent to organisation admins may temporarily display default branding.
          </span>
        </WhitelabelEmailWarning>

        <div
          class="tw-w-full tw-relative tw-transition-opacity tw-duration-200"
          :class="{ 'tw-opacity-60': isDisabled }"
        >
          <CustomMultiselect
            v-model="selectedRecipients"
            v-tooltip="{
              placement: 'bottom',
              theme: 'dark-blue-tooltip',
              content: 'Agency email notifications are turned off, so no recipients can be selected until notifications are enabled',
              disabled: !isDisabled
            }"
            class="tw-text-left tw-h-12.5 tw-relative tw-w-full"
            :classes="disabledSelectClasses"
            :close-on-select="false"
            mode="tags"
            :options="selectOptions"
            :searchable="true"
            :disabled="isDisabled || isLoading || saving"
            placeholder="Select recipients..."
            @close="save"
            @deselect="save"
          />
        </div>
      </div>
    </div>
  </div>
</template>
